/**
 * Header bar for the plugin builder editor page.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, Save, Upload, Loader2, Coins, EllipsisVertical } from 'lucide-react';
import type { PluginBuilderSession, VirtualFileSystem } from '../types';

interface PluginBuilderHeaderProps {
  session: PluginBuilderSession | null;
  files: VirtualFileSystem | null;
  onSave: (name: string, description?: string) => Promise<void>;
  onPublish: () => Promise<void>;
  isSaving: boolean;
  isPublishing: boolean;
  className?: string;
}

export function PluginBuilderHeader({
  session,
  files,
  onSave,
  onPublish,
  isSaving,
  isPublishing,
  className,
}: PluginBuilderHeaderProps) {
  const navigate = useNavigate();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [name, setName] = useState(session?.currentName || '');
  const [description, setDescription] = useState(session?.currentDescription || '');

  const handleSave = async () => {
    if (!name.trim()) return;
    await onSave(name, description || undefined);
    setSaveDialogOpen(false);
  };

  const openSaveDialog = () => {
    setName(session?.currentName || '');
    setDescription(session?.currentDescription || '');
    setSaveDialogOpen(true);
  };

  const handlePublish = async () => {
    await onPublish();
    setPublishDialogOpen(false);
  };

  const formatCost = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  const hasFiles = files && Object.keys(files).length > 0;
  const hasManifest = files && files['fias-plugin.json'];

  return (
    <div className={cn('flex items-center justify-between border-b px-4 py-2', className)}>
      {/* Left: Back and title */}
      <div className="flex items-center gap-3 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          onClick={() => navigate('/a/arche_plugin_builder')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold truncate max-w-[150px] sm:max-w-none">
            {session?.currentName || 'Untitled Plugin'}
          </h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{session?.status === 'active' ? 'Editing' : session?.status}</span>
          </div>
        </div>
      </div>

      {/* Center: Cost indicator — desktop */}
      <div className="hidden xl:flex items-center gap-2 text-sm text-muted-foreground">
        <Coins className="h-4 w-4" />
        <span>{formatCost(session?.totalCostCents || 0)} credits used</span>
      </div>

      {/* Mobile: Compact cost badge */}
      <div className="flex xl:hidden items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
        <Coins className="h-3.5 w-3.5" />
        <span>{formatCost(session?.totalCostCents || 0)}</span>
      </div>

      {/* Right: Actions — desktop */}
      <div className="hidden xl:flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={openSaveDialog} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Draft
        </Button>

        <Button
          size="sm"
          onClick={() => setPublishDialogOpen(true)}
          disabled={!hasFiles || !hasManifest || isPublishing}
        >
          {isPublishing ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Upload className="h-4 w-4 mr-2" />
          )}
          Publish
        </Button>
      </div>

      {/* Right: Actions — mobile dropdown */}
      <div className="xl:hidden flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <EllipsisVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={openSaveDialog} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Draft
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setPublishDialogOpen(true)}
              disabled={!hasFiles || !hasManifest || isPublishing}
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Publish
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Plugin</DialogTitle>
            <DialogDescription>
              Save your plugin as a draft. You can continue editing it later.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="plugin-name" className="text-sm font-medium">
                Name
              </label>
              <Input
                id="plugin-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Plugin"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="plugin-description" className="text-sm font-medium">
                Description
              </label>
              <Textarea
                id="plugin-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this plugin do?"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!name.trim() || isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Plugin</DialogTitle>
            <DialogDescription>
              Submit your plugin for review. Once approved, it will be available on the marketplace.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Your plugin files will be packaged, uploaded, and submitted for AI-powered review.
              This typically takes 1-2 minutes.
            </p>
            <p className="text-sm font-medium">
              Publishing fee: $10.00 (charged when approved and listed)
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={isPublishing}>
              {isPublishing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
